import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import SelectBox from '../../shared/component/SelectBox';
import { NormalSmallTextArea } from '../../shared/component/headless/textarea/TextAreaBody';
import { WorkViewerTitle } from '../../shared/component/headless/input/InputBody';
import { TextBlockData, TextData } from '../../shared/dto/EntityRepository';
import { DisplayAlignment, DisplaySize, ModalType, SelectType, TextBlockType } from '../../shared/enum/EnumRepository';
import { PEHeader, PEIndicator, SelectBoxContainer, SelectBoxWrapper } from './Work';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import ConstrainedRichText from '../../shared/richText/ConstrainedRichText';
import { 
  TEXTBLOCK_PARAGRAPH_MAX_LENGTH,
  TEXTBLOCK_CAPTION_MAX_LENGTH } from '../../shared/constants/limits';
import { WorkViewerInfo } from '../../shared/component/headless/input/InputBody';
import importIcon from '../../asset/icon/import.png';
import deleteIcon from '../../asset/icon/delete_close.png';
import { useProjectElementActions } from '../../shared/hooks/useApi/useProjectElement';
import { useModalStore } from '../../shared/store/portal/modalStore';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import OptimizedMoleculeImg from '../../shared/component/molecule/OptimizedMoleculeImg';
import { convertS3UrlToCloudFrontUrl } from '../../shared/aws/awsHelper';
import { StyledImgProps } from '../../shared/dto/StyleCompRepository';
import defaultImage from '../../asset/project/default_1.png';
import MoleculeTextBlockLink from '../../shared/component/molecule/MoleculeTextBlockLink';
import { getTextBlockLinkCaption } from '../../shared/util/textBlockLinkCaption';


export interface TextProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  text: TextData; 
  selectedTextBlocks: TextBlockData[];
}

const Text: React.FC<TextProps> = ({ peId, alignment, displaySize, text, selectedTextBlocks }) => {
  const { isEditMode } = useEditMode();
  const handleDisplayAlignmentChange = useProjectElementListStore((s) => s.updateDisplayAlignment);
  const handleDisplaySizeChange = useProjectElementListStore((s) => s.updateDisplaySize);
  const updateText = useProjectElementListStore((s) => s.updateText);
  const updateTextBlock = useProjectElementListStore((s) => s.updateTextBlock);
  const { deleteTextBlockInPE } = useProjectElementActions();
  const { setStandardModal } = useModalStore();

  const handleLinkCaptionChange = (textBlockId: string) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateTextBlock(peId, textBlockId, { caption: e.target.value });
  };

  const handleParagraphChange = (textBlockId: string) => (next: string) => {
    updateTextBlock(peId, textBlockId, {
      textContent: next,
    });
  };

  const handleImageChange = (textBlockId: string, uploadFileId: string) => (originUrl: string, width: number, height: number) => {
    updateTextBlock(peId, textBlockId, {
      uploadFile: {
        id: uploadFileId,
        allowDownload: false,
        originUrl,
        width,
        height,
      },
      imageChanged: true,
    });
  };

  const handleChangeCaption = (textBlockId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    updateTextBlock(peId, textBlockId, { caption: e.target.value });
  };

  const handleChangeLinkUrl = (textBlockId: string) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateTextBlock(peId, textBlockId, { url: e.target.value });
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateText(peId, { title: e.target.value });
  };

  const handleSubtitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateText(peId, { subtitle: e.target.value });
  };

  const handleAuthorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateText(peId, { source: { author: e.target.value } });
  };

  const handleImportTextBlock = async() => {
    setStandardModal({
      modalType: ModalType.IMPORT_STATION_FOR_TEXT_BLOCK,
      title: peId,
      value: text.id,
      handleChange: () => { }
    });
  };

  const handleDeleteTextBlock = async (textBlockId: string) => {
    if (selectedTextBlocks.length === 1) return;

    try {
      await deleteTextBlockInPE({
        projectElementId: peId,
        textBlockId: textBlockId,
      });
    } catch (err) {
    }
  };

  const renderTextBlock = (data: TextBlockData) => {
    switch (data.type) {
      case TextBlockType.PARAGRAPH:
        return (
          <ConstrainedRichText
            value={data.textContent}
            placeholder={"Enter description"}
            onChange={handleParagraphChange(data.id)}
            StyledView={RichParagraph}
            maxLength={TEXTBLOCK_PARAGRAPH_MAX_LENGTH}
            maxRows={100}
          />
        );
      case TextBlockType.LINK:
        return (
          <MoleculeTextBlockLink
            label={getTextBlockLinkCaption(data)}
            url={data.url || ""}
            handleChangeLabel={handleLinkCaptionChange(data.id)}
            handleChangeUrl={handleChangeLinkUrl(data.id)}
            StyledLabel={Paragraph}
            StyledLabelTextArea={ParagraphTextArea}
          />
        );
      case TextBlockType.IMAGE:
        let imageUrl = defaultImage;
        const uploadFile = data.uploadFile;
        if (uploadFile && uploadFile.originUrl) {
          imageUrl = convertS3UrlToCloudFrontUrl(uploadFile.originUrl);
        }
        return (
          <TextBlockImageWrapper>
            <ImageWrapper>
              <OptimizedMoleculeImg
                srcUrl={imageUrl}
                alt={"Image Block"}
                displaySize={DisplaySize.REGULAR}
                displayAlignment={DisplayAlignment.CENTER}
                handleChange={handleImageChange(data.id, data.uploadFile?.id ?? '')}
                StyledImg={WorkImage as React.ComponentType<StyledImgProps>}
                priority={true}
              />
            </ImageWrapper>
            <CaptionWrapper>
              <MoleculeInputDiv
                value={data.caption || ""}
                placeholder={"Caption"}
                handleChange={handleChangeCaption(data.id)}
                inputStyle={CaptionInput}
                StyledDiv={Caption}
                maxLength={TEXTBLOCK_CAPTION_MAX_LENGTH}
              />
            </CaptionWrapper>
          </TextBlockImageWrapper>
        );
      default:
        return null;
    }
  }

  const defaultAlignment = alignment || DisplayAlignment.CENTER;
  const defaultDisplaySize = displaySize || DisplaySize.BIG;

  const leftFlexGrow = useMemo(() => getLeftFlexGrow(defaultAlignment), [defaultAlignment]);
  const rightFlexGrow = useMemo(() => getRightFlexGrow(defaultAlignment), [defaultAlignment]);

  const inlineInfo =
  `${text.subtitle ? `-${text.subtitle}, ` : ", "}${text.source.author}, ${text.prodYear}`;


  return (
    <TextComp id={`project-element-text-${peId}`}>
      {isEditMode &&
      <PEHeader>
        <SelectBoxContainer>
          <SelectBoxWrapper>
            <SelectBox
              value={defaultDisplaySize}
              selectType={SelectType.WORK_SIZE}
              handleChange={(value) => handleDisplaySizeChange(peId, value)}
              direction={false} />
          </SelectBoxWrapper>
          <SelectBoxWrapper>
            <SelectBox
              value={defaultAlignment}
              selectType={SelectType.DISPLAY_ALIGNMENT}
              handleChange={(value) => handleDisplayAlignmentChange(peId, value)}
              direction={false} />
          </SelectBoxWrapper>
        </SelectBoxContainer>
        <PEIndicator peId={peId}>: Text</PEIndicator>
        </PEHeader>
      }
      <PETextWrapper>
        <PETextSpacerLeft $flexGrow={leftFlexGrow} />
        <TextAndTextBlockWrapper
            $alignment={defaultAlignment}
            $displaySize={defaultDisplaySize}>
            <TextInfoWrapper $isEditMode={isEditMode}>
              {isEditMode && (
                <>
              <TitleWrapper>
                <MoleculeInputDiv
                  value={text.title}
                  defaultValue={"Title"}
                  placeholder={"Title"}
                  handleChange={handleTitleChange}
                  inputStyle={WorkViewerTitle}
                  StyledDiv={Title}
                />
                {(text.subtitle || isEditMode ) &&
                  <MoleculeInputDiv
                    value={text.subtitle || ""}
                    defaultValue={"Subtitle"}
                    placeholder={"Subtitle"}
                    handleChange={handleSubtitleChange}
                    inputStyle={WorkViewerInfo}
                    StyledDiv={SubTitle}
                    />
                }
              </TitleWrapper>
              <MoleculeInputDiv
                value={text.source.author}
                defaultValue={"Author"}
                placeholder={"Author"}
                handleChange={handleAuthorChange}
                inputStyle={WorkViewerInfo}
                StyledDiv={Info}
                />
                </>
              )
              }
            </TextInfoWrapper>
          <TextBlocksContainer>
            {selectedTextBlocks.map((tb) => (
              <TextBlockWrapper key={tb.id}>
              {renderTextBlock(tb)}
              {isEditMode && selectedTextBlocks.length > 1 &&
                <BtnGroup>
                  <UniversalIconBtn
                    size="fit"
                    border={false}
                    backgroundImg={deleteIcon}
                    onClick={() => handleDeleteTextBlock(tb.id)}
                  />
                </BtnGroup>
                }
              </TextBlockWrapper>
            ))}
            {isEditMode &&
              <>
                <BtnGroup>
                  <UniversalIconBtn
                    size="fit"
                    border={false}
                    backgroundImg={importIcon}
                    onClick={handleImportTextBlock}
                  />
                </BtnGroup>
              </>
            }
          </TextBlocksContainer>
          <InlineInfo>
            <TitleOblique>{text.title} </TitleOblique>{inlineInfo}
          </InlineInfo>
        </TextAndTextBlockWrapper>
        <PETextSpacerRight $flexGrow={rightFlexGrow} />
      </PETextWrapper>
    </TextComp>
  );
}

const getLeftFlexGrow = ($displayAlignment: DisplayAlignment): string => {
  switch ($displayAlignment) {
    case DisplayAlignment.LEFT_FLUSH:     // ┏text──────┛
      return '0';
    case DisplayAlignment.LEFT_OFFSET:    // ┏──¼│text───┛
      return '2';
    case DisplayAlignment.RIGHT_OFFSET:   // ┏───text│¼──┛
      return '6';
    case DisplayAlignment.RIGHT_FLUSH:    // ┏──────text┛
      return '1';
    case DisplayAlignment.CENTER:         // ┏──½│text│½──┛
    default:
      return '1';
  }
};

const getRightFlexGrow = ($displayAlignment: DisplayAlignment): string => {
  switch ($displayAlignment) {
    case DisplayAlignment.LEFT_FLUSH:     // ┏text──────┛
      return '1';
    case DisplayAlignment.LEFT_OFFSET:    // ┏──¼│text───┛
      return '6';
    case DisplayAlignment.RIGHT_OFFSET:   // ┏───text│¼──┛
      return '2';
    case DisplayAlignment.RIGHT_FLUSH:    // ┏──────text┛
      return '0';
    case DisplayAlignment.CENTER:         // ┏──½│text│½──┛
    default:
      return '1';
  }
};

export const TextComp = styled.div`
  position: relative;
  min-height: 80px;
`;

const PETextWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;

const PETextSpacerLeft = styled.div<{ $flexGrow: string }>`
  display: flex;
  flex: ${({ $flexGrow }) => `${$flexGrow} 1 0%`};
  min-width: 0;
  transition: all 0.3s ease;
`;

const PETextSpacerRight = styled.div<{ $flexGrow: string }>`
  display: flex;
  flex: ${({ $flexGrow }) => `${$flexGrow} 1 0%`};
  min-width: 0;
  transition: all 0.3s ease;
`;

const TextAndTextBlockWrapper = styled.div<{ $alignment: DisplayAlignment, $displaySize: DisplaySize }>`
  flex-grow: 0;
  flex-shrink: 0;
  width: ${({ $displaySize }) => {
    switch ($displaySize) {
      case DisplaySize.WIDE:
        return 'clamp(750px, 60vw, 800px)';
      case DisplaySize.BIG:
        return 'clamp(700px, 55vw, 750px)';
      case DisplaySize.MEDIUM:
        return 'clamp(600px, 50vw, 650px)';
      case DisplaySize.REGULAR:
        return 'clamp(500px, 45vw, 550px)';
      case DisplaySize.SMALL:
        return 'clamp(400px, 40vw, 450px)';
      default:
        return 'clamp(600px, 50vw, 650px)';
    }
  }};

  transition: all 0.3s ease;
`;

const TextInfoWrapper = styled.div<{ $isEditMode: boolean }>`
  display: flex;
  height: ${({ $isEditMode }) => $isEditMode ? '100px' : '0'};
  flex-direction: column;
  justify-content: flex-start;
  margin-bottom: 10px;
  overflow: hidden;
  transition: height 0.6s ease;
`;

const InlineInfo = styled.div`
  width: 100%;
  height: 30px;
  overflow-y: hidden;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  margin-top: 6px;
  ${({ theme }) => theme.typography.Body_03_2};
  transition: height 0.4s ease;
`;

const TitleOblique = styled.span`
  font-style: oblique;
  margin-right: 5px;
`;

const TitleWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin-bottom: 10px;
`;

const Title = styled.h2`
  display: flex;
  align-items: center;

  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const SubTitle = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const Info = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const TextBlocksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TextBlockWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  --rich-text-size-s: ${({ theme }) => theme.fontSize.font_B04};
  --rich-text-size-l: ${({ theme }) => theme.fontSize.font_B02};
`;

const TextBlockImageWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CaptionWrapper = styled.div`
  width: 70%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Caption = styled.div`
  width: 100%;
  padding: 4px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_06};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`

const CaptionInput = styled(WorkViewerInfo)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const WorkImage = styled.img`
  width: 70%;
  height: 70%;
  object-fit: contain;
`;

const RichParagraph = styled.div<{ $isEditMode?: boolean }>`
  width: 100%;
  padding: 12px 0 4px 0;
  border-bottom: ${({ $isEditMode, theme }) => $isEditMode ? `1px solid ${theme.colors.color_Gray_05}` : 'none'};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
  line-height: 1;
`

const Paragraph = styled.div`
  width: 100%;
  padding: 10px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  line-height: 1.7;
  ${({ theme }) => theme.typography.Body_03_2};
`

const ParagraphTextArea = styled(NormalSmallTextArea)`
  text-align: left;
  line-height: 1.7;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_06};
  &::-webkit-scrollbar {
    display: none;
  }
  ${({ theme }) => theme.typography.Body_03_2};
`;

const BtnGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.4vw;
`;

const BtnWrapper = styled.div`
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  background-color: ${({ theme }) => theme.colors.color_Alpha_03};
  backdrop-filter: blur(4px);
  cursor: pointer;
  &:hover {
    transform: scale(1.05);
  }
  transition: all 0.3s ease-in-out;
`;

const IconBtn = styled.div<{ $backgroundImg: string }>`
  width: clamp(10px, 2vw, 12px);
  height: clamp(10px, 2vw, 12px);
  background-image: url(${({ $backgroundImg }) => $backgroundImg});
  background-size: cover;
  background-position: center;
`;

export default Text;
